"use client";

import type { DirectoryBadge } from "@/lib/directory-badges";

import { useDirectoryBadges } from "./directory-badges-context";

const DIRECTORY_BADGES_MARQUEE_MIN_ITEMS = 6;

export default function FooterDirectoryBadges() {
  const badges = useDirectoryBadges();

  if (badges.length === 0) {
    return null;
  }

  const isMarquee = badges.length >= DIRECTORY_BADGES_MARQUEE_MIN_ITEMS;
  const marqueeFlag = isMarquee ? "true" : "false";

  return (
    <div
      className="mt-8 border-t border-white/10 pt-6"
      data-testid="mirofish-footer-badges"
      data-count={badges.length}
      data-marquee={marqueeFlag}
    >
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
        Featured directories
      </p>
      <div
        className="directory-badge-viewport"
        data-marquee={marqueeFlag}
        data-testid="mirofish-footer-badges-viewport"
      >
        <div className="directory-badge-track" data-marquee={marqueeFlag}>
          <div className="directory-badge-set">
            {badges.map((badge) => (
              <DirectoryBadgeLink key={badge.href} badge={badge} />
            ))}
          </div>
          {isMarquee ? (
            <div aria-hidden="true" className="directory-badge-set">
              {badges.map((badge) => (
                <DirectoryBadgeLink
                  key={`duplicate-${badge.href}`}
                  badge={badge}
                  duplicate
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DirectoryBadgeLink({
  badge,
  duplicate = false,
}: {
  badge: DirectoryBadge;
  duplicate?: boolean;
}) {
  return (
    <a
      href={badge.href}
      target="_blank"
      rel={badge.rel ?? "noopener noreferrer"}
      title={badge.title}
      aria-label={duplicate ? undefined : badge.label}
      tabIndex={duplicate ? -1 : undefined}
      className="inline-flex h-[60px] max-w-[180px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.06] px-3 py-2 opacity-90 transition hover:-translate-y-0.5 hover:border-white/25 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 motion-reduce:transform-none"
    >
      {badge.imageSrc ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={badge.imageSrc}
          alt={duplicate ? "" : badge.label}
          width="160"
          className="max-h-10 w-auto max-w-full object-contain"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="truncate px-2 py-1 text-sm font-semibold text-white/80">
          {badge.text}
        </span>
      )}
    </a>
  );
}
